import React from 'react';
import { Link } from 'react-router-dom';
import {
  Alert, Button, ErrorState, IconButton, KpiCard, PageHeader,
  SectionCard, SegmentedControl, SkeletonCard, SkeletonText,
} from '@parsina/framework';
import { C, chartPalette, funnelColors } from '@parsina/framework/tokens';
import {
  AlertTriangle, Building2, Coins, Layers, RefreshCw, ShieldCheck, TrendingUp,
} from 'lucide-react';

import { useTranslation } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useUrlState } from '../../lib/urlState';
import { statusLabelKey, tierLabelKey, UNPROVISIONED } from '../../constants/subscription';
import { isIrr, moneyUnit } from '../../lib/format';
import GrowthChart from '../../components/GrowthChart';
import BreakdownList from '../../components/BreakdownList';

/**
 * Archetype A (Cockpit) — the estate at a glance.
 *
 * ① PageHeader + context widget · ② alerts · ③ KPI strip · ⑤ P2 main+rail.
 *
 * ── EVERY NUMBER IS A DOOR ──────────────────────────────────────────────────
 * A KPI or a breakdown row names a filtered list that already exists one click
 * away, so each one that can be is a link. Reading «۴ بدون اشتراک» and then having
 * to go and rebuild that filter by hand is the panel making the operator do its
 * work.
 *
 * ── TWO CHARTS, NOT ONE WITH TWO AXES ───────────────────────────────────────
 * Counts and money share the x-domain and nothing else. A dual axis would make
 * every visual comparison between them meaningless, which is worse than two
 * charts. `revenueRial` and `lifetimeRevenueRial` were fetched and rendered
 * nowhere at all before this.
 */
const METRICS_QUERY = `
  query GrowthMetrics($from: String, $bucket: String) {
    growthMetrics(from: $from, bucket: $bucket) {
      buckets {
        label signups activations renewals expirations reactivations revenueRial
      }
      totalClinics trialing active pastDue expired suspended neverExpires
      perpetual unprovisioned
      tiers { capabilitySet tierNameFa clinics mrrRial }
      mrrRial lifetimeRevenueRial trialConversionPct currency
    }
  }
`;

const RANGES = ['6m', '12m', 'all'];
const BUCKETS = ['month', 'week'];

const SCHEMA = {
  range: { default: '12m', values: RANGES },
  bucket: { default: 'month', values: BUCKETS },
};

/** `range` → the ISO instant to ask the service from. `all` means no bound. */
function rangeStart(range) {
  if (range === 'all') return null;
  const months = range === '6m' ? 6 : 12;
  const d = new Date();
  d.setMonth(d.getMonth() - months);
  return d.toISOString();
}

export default function Dashboard() {
  const {
    t, locale, formatNumber, formatMoney, formatTime, formatPercent, formatList,
  } = useTranslation();
  useDocumentTitle('nav.title.dashboard');

  const [view, setView] = useUrlState(SCHEMA);
  const [metrics, setMetrics] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [updatedAt, setUpdatedAt] = React.useState(null);

  const load = React.useCallback(({ quiet = false } = {}) => {
    if (quiet) setRefreshing(true); else setLoading(true);
    setError(null);
    gql(METRICS_QUERY, { from: rangeStart(view.range), bucket: view.bucket })
      .then((d) => { setMetrics(d?.growthMetrics || null); setUpdatedAt(Date.now()); })
      .catch((e) => setError(errorMessage(e, t)))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [view.range, view.bucket, t]);

  // A range or bucket change is a REFETCH, not a fresh page: the content dims
  // rather than reverting to skeletons, so the operator's eye stays where it was
  // (§2.4 stale-while-refetch).
  const first = React.useRef(true);
  React.useEffect(() => {
    load({ quiet: !first.current });
    first.current = false;
  }, [load]);

  const bucketWord = t(`dashboard.bucket.${view.bucket}`);

  const contextWidget = (
    <>
      <SegmentedControl
        semantics="filter"
        size="sm"
        ariaLabel={t('dashboard.range.label')}
        options={RANGES.map((value) => ({ value, label: t(`dashboard.range.${value}`) }))}
        value={view.range}
        onChange={(range) => setView({ range })}
        data-testid="dashboard-range"
      />
      <SegmentedControl
        semantics="filter"
        size="sm"
        ariaLabel={t('dashboard.bucket.label')}
        options={BUCKETS.map((value) => ({ value, label: t(`dashboard.bucket.${value}`) }))}
        value={view.bucket}
        onChange={(bucket) => setView({ bucket })}
        data-testid="dashboard-bucket"
      />
      <IconButton
        variant="ghost"
        icon={<RefreshCw size={16} />}
        label={t('dashboard.refresh')}
        title={updatedAt ? t('dashboard.updatedAt', { time: formatTime(updatedAt) }) : t('dashboard.refresh')}
        onClick={() => load({ quiet: true })}
        data-testid="dashboard-refresh"
      />
    </>
  );

  return (
    <div className="flex h-full flex-col bg-background" data-testid="dashboard">
      <PageHeader title={t('dashboard.title')} sub={t('dashboard.sub')}>
        {contextWidget}
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        {/* Width-capped and centred: without a cap the five KPI cards stretch
            across a fifth of an ultra-wide monitor each. */}
        <div className="mx-auto w-full max-w-[1440px]">
          {loading ? (
            <DashboardSkeleton />
          ) : error || !metrics ? (
            <ErrorState
              title={t('errors.region.dashboard')}
              description={error || t('errors.region.noAnswer')}
              onRetry={load}
              retryLabel={t('common.actions.retry')}
              data-testid="dashboard-error"
            />
          ) : (
            <DashboardContent
              metrics={metrics}
              bucketWord={bucketWord}
              refreshing={refreshing}
              t={t}
              locale={locale}
              formatNumber={formatNumber}
              formatMoney={formatMoney}
              formatPercent={formatPercent}
              formatList={formatList}
            />
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Mirrors the real layout: five KPI blocks in the same grid, a chart-height card
 * in main, two rail cards. A skeleton of a different shape makes the content jump
 * when it lands, which is the thing a skeleton exists to prevent (§2.4).
 */
function DashboardSkeleton() {
  return (
    <div className="space-y-5" data-testid="dashboard-loading" data-state="loading" aria-busy="true">
      <div className="grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className="rounded-xl border border-border bg-card p-4">
            <SkeletonText width="60%" height={22} />
            <div className="mt-3"><SkeletonText width="80%" height={11} /></div>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <SkeletonCard />
        <div className="space-y-5"><SkeletonCard /><SkeletonCard /></div>
      </div>
    </div>
  );
}

function DashboardContent({
  metrics, bucketWord, refreshing, t, locale,
  formatNumber, formatMoney: formatMinor, formatPercent, formatList,
}) {
  // Every money figure here is in the metrics' ONE currency (PM-0211): IRR on
  // Parsina, which reads exactly as before, the deployment's platform currency
  // elsewhere. The service refuses a mixed set rather than summing one.
  const { currency } = metrics;
  const formatMoney = (minor) => formatMinor(minor, { currency });
  const unit = moneyUnit(currency, { t, locale });

  // The service can only count what it holds rows for, so an un-provisioned clinic
  // is invisible to it — the total has to add the gap back in or the estate looks
  // complete when it is not.
  const totalClinics = metrics.totalClinics + metrics.unprovisioned;
  const attention = metrics.pastDue + metrics.expired + metrics.suspended;
  const hasHistory = !!metrics.buckets?.length;

  // On day one the operator's actual job is to provision the estate. Saying that
  // outright is better than a wall of zeros — and it takes the PLACE of the
  // un-provisioned alert rather than adding to it, so the screen keeps exactly one
  // primary action (§10).
  const showGettingStarted = !hasHistory && metrics.unprovisioned > 0;

  const kpis = [
    {
      key: 'total',
      label: t('dashboard.kpi.total'),
      value: formatNumber(totalClinics),
      icon: <Building2 size={20} color={C.navy} />,
      bg: C.navyLight,
      href: '/clinics',
    },
    {
      key: 'inGoodStanding',
      // Deliberately NOT «با دسترسی»: `growthMetrics` returns no entitled count,
      // and a label that claims to measure entitlement while adding two statuses
      // would be a number nobody could reconcile.
      label: t('dashboard.kpi.inGoodStanding'),
      value: formatNumber(metrics.active + metrics.trialing),
      icon: <ShieldCheck size={20} color={C.green} />,
      bg: C.greenLight,
      href: '/clinics?status=ACTIVE',
    },
    {
      key: 'mrr',
      label: t('dashboard.kpi.mrr'),
      value: formatMoney(metrics.mrrRial),
      unit,
      icon: <Coins size={20} color={C.teal} />,
      bg: C.tealLight,
    },
    {
      key: 'conversion',
      label: t('dashboard.kpi.conversion'),
      // -1 means no trial has RESOLVED yet. «۰٪» there would report a failure
      // that has not happened; the dash says "nothing to measure", and the
      // tooltip says why, because an unexplained dash reads as broken.
      // `formatPercent` takes a PERCENTAGE (55 → «۵۵٪») and does the ÷100 itself,
      // so the service's figure goes in as it arrives.
      value: metrics.trialConversionPct < 0
        ? t('common.value.none')
        : formatPercent(metrics.trialConversionPct),
      hint: metrics.trialConversionPct < 0 ? t('dashboard.kpi.conversionHint') : undefined,
      icon: <TrendingUp size={20} color={C.green} />,
      bg: C.greenLight,
    },
    {
      key: 'attention',
      label: t('dashboard.kpi.attention'),
      value: formatNumber(attention),
      sub: t('dashboard.kpi.attentionSplit', {
        pastDue: formatNumber(metrics.pastDue),
        expired: formatNumber(metrics.expired),
        suspended: formatNumber(metrics.suspended),
      }),
      icon: <AlertTriangle size={20} color={C.amber} />,
      bg: C.amberLight,
      href: '/clinics?status=EXPIRED',
    },
  ];

  const statusRows = [
    { key: 'active', label: t(statusLabelKey('ACTIVE')), value: metrics.active, tone: C.green, href: '/clinics?status=ACTIVE' },
    { key: 'trialing', label: t(statusLabelKey('TRIALING')), value: metrics.trialing, tone: C.navy, href: '/clinics?status=TRIALING' },
    { key: 'pastDue', label: t(statusLabelKey('PAST_DUE')), value: metrics.pastDue, tone: C.amber, href: '/clinics?status=PAST_DUE' },
    { key: 'expired', label: t(statusLabelKey('EXPIRED')), value: metrics.expired, tone: C.red, href: '/clinics?status=EXPIRED' },
    { key: 'suspended', label: t(statusLabelKey('SUSPENDED')), value: metrics.suspended, tone: C.red, href: '/clinics?status=SUSPENDED' },
    // No status filter expresses either of these, so they are plain rows rather
    // than links that would land on a list the operator cannot recognise.
    { key: 'neverExpires', label: t(statusLabelKey('NEVER_EXPIRES')), value: metrics.neverExpires, tone: C.textSecondary },
    { key: 'perpetual', label: t(statusLabelKey('PERPETUAL')), value: metrics.perpetual, tone: C.textSecondary },
    { key: 'unprovisioned', label: t(statusLabelKey(UNPROVISIONED)), value: metrics.unprovisioned, tone: C.textMuted, href: `/clinics?status=${UNPROVISIONED}` },
  ];

  // ZERO ROWS ARE HIDDEN. Listing all eight always meant a young platform rendered
  // seven empty grey tracks and one filled bar, which reads as a broken widget
  // rather than "everything is in one bucket". What is present is shown; what is
  // absent is summarised in one line underneath.
  const present = statusRows.filter((r) => r.value > 0);
  const absent = statusRows.filter((r) => r.value === 0);
  const statusTotal = present.reduce((sum, r) => sum + r.value, 0);

  const tierTotal = (metrics.tiers || []).reduce((sum, x) => sum + (x.clinics || 0), 0);
  const TIER_TONE = {
    CLINIC_BASIC: C.textMuted,
    CLINIC_SILVER: C.navy,
    CLINIC_GOLD: C.teal,
  };

  const series = [
    { key: 'signups', label: t('dashboard.growth.series.signups'), color: funnelColors.acquired },
    { key: 'activations', label: t('dashboard.growth.series.activations'), color: funnelColors.converted },
    { key: 'renewals', label: t('dashboard.growth.series.renewals'), color: chartPalette[3] },
    { key: 'expirations', label: t('dashboard.growth.series.expirations'), color: funnelColors.lost },
  ];

  const reactivations = (metrics.buckets || []).reduce((sum, b) => sum + (b.reactivations || 0), 0);

  return (
    <div style={{ opacity: refreshing ? 0.45 : 1 }} aria-busy={refreshing || undefined} className="transition-opacity">
      {/* ② Alert zone — rationed to two, in priority order (§10). */}
      <div className="flex flex-col gap-3">
        {metrics.unprovisioned > 0 && !showGettingStarted ? (
          <Alert
            variant="warning"
            data-testid="dashboard-unprovisioned"
            action={(
              // `outline`, not the default fill: the KPI strip and the alert must
              // not both read as the primary thing to press.
              <Button asChild variant="outline" size="sm">
                <Link to={`/clinics?status=${UNPROVISIONED}`}>{t('common.actions.view')}</Link>
              </Button>
            )}
          >
            {t('dashboard.alerts.unprovisioned', { count: metrics.unprovisioned })}
          </Alert>
        ) : null}

        {metrics.pastDue > 0 ? (
          <Alert
            variant="info"
            data-testid="dashboard-pastdue"
            action={(
              <Button asChild variant="outline" size="sm">
                <Link to="/clinics?status=PAST_DUE">{t('common.actions.view')}</Link>
              </Button>
            )}
          >
            {t('dashboard.alerts.pastDue', { count: metrics.pastDue })}
          </Alert>
        ) : null}
      </div>

      {/* ③ KPI strip. `minWidth={0}` so five cards compress inside the grid
          instead of forcing it wider than the page (§4.2). */}
      <div className="mt-5 grid grid-cols-2 gap-3.5 md:grid-cols-3 xl:grid-cols-5">
        {kpis.map((kpi) => {
          const card = (
            <KpiCard
              minWidth={0}
              label={(
                <span className="block">
                  <span className="block">{kpi.label}</span>
                  {kpi.sub ? (
                    <span className="mt-0.5 block text-[10.5px] text-muted-foreground">{kpi.sub}</span>
                  ) : null}
                </span>
              )}
              value={kpi.value}
              unit={kpi.unit}
              icon={kpi.icon}
              bg={kpi.bg}
            />
          );
          // A linked KPI is a REAL `<a href>`, not a card with a click handler:
          // it goes somewhere, so middle-click, ⌘-click and "copy link" have to
          // work, and a keyboard user needs it in the tab order with link
          // semantics rather than as a button that happens to navigate.
          return kpi.href ? (
            <Link
              key={kpi.key}
              to={kpi.href}
              className="rounded-xl outline-none transition-shadow focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              title={kpi.hint}
              data-testid={`dashboard-kpi-${kpi.key}`}
            >
              {card}
            </Link>
          ) : (
            <div key={kpi.key} title={kpi.hint} data-testid={`dashboard-kpi-${kpi.key}`}>
              {card}
            </div>
          );
        })}
      </div>

      {/* ⑤ P2 — the funnel is what you scan, the mix is what you check. */}
      <div className="mt-5 grid grid-cols-1 gap-5 xl:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)]">
        <div className="flex min-w-0 flex-col gap-5">
          <SectionCard
            title={t('dashboard.growth.title')}
            sub={t('dashboard.growth.sub', { bucket: bucketWord })}
            icon={TrendingUp}
          >
            <GrowthChart
              buckets={metrics.buckets}
              series={series}
              ariaLabel={t('dashboard.growth.aria')}
            />
            {/* Reactivations are a real part of the funnel and would have been a
                fifth bar nobody could tell from «activations» at this width, so
                they are stated instead of drawn. */}
            {reactivations > 0 ? (
              <p className="text-xs text-muted-foreground">
                {t('dashboard.growth.reactivations', { count: reactivations })}
              </p>
            ) : null}
          </SectionCard>

          <SectionCard
            title={t('dashboard.revenue.title')}
            sub={t('dashboard.revenue.sub', { bucket: bucketWord })}
            icon={Coins}
            data-testid="dashboard-revenue-chart"
          >
            <GrowthChart
              buckets={metrics.buckets}
              series={[{ key: 'revenueRial', label: t('dashboard.revenue.series'), color: C.teal }]}
              // Money, so the axis and the bar labels go through the money
              // formatter — minor units of the metrics' currency in (rials →
              // toman for IRR).
              formatValue={(v) => formatMoney(v)}
              ariaLabel={t('dashboard.revenue.aria')}
            />
            <p className="text-xs text-muted-foreground">
              {t('dashboard.revenue.lifetime', {
                amount: formatMoney(metrics.lifetimeRevenueRial),
                unit,
              })}
            </p>
          </SectionCard>
        </div>

        <div className="flex min-w-0 flex-col gap-5">
          <SectionCard title={t('dashboard.statusMix.title')} icon={Building2}>
            {statusTotal === 0 ? (
              <p className="py-2 text-sm text-muted-foreground" data-testid="status-breakdown-empty">
                {t('dashboard.statusMix.empty')}
              </p>
            ) : (
              <BreakdownList
                data-testid="status-breakdown"
                rows={present.map((r) => ({
                  key: r.key,
                  label: r.label,
                  value: formatNumber(r.value),
                  percent: Math.round((r.value / statusTotal) * 100),
                  tone: r.tone,
                  href: r.href,
                }))}
                // `formatList`, not `join('، ')`: the separator and the final
                // conjunction are locale data (Persian «، », English ", " and an
                // "and"), and a hardcoded one is a Persian comma inside an
                // English sentence.
                note={absent.length
                  ? t('dashboard.statusMix.absent', { list: formatList(absent.map((r) => r.label)) })
                  : undefined}
              />
            )}
          </SectionCard>

          {/* Beneath the status mix, not beside it: the two answer different
              questions about the same estate and are read in that order — is the
              clinic in good standing, then what did it buy. */}
          <SectionCard
            title={t('dashboard.tierMix.title')}
            sub={t('dashboard.tierMix.sub')}
            icon={Layers}
          >
            {!metrics.tiers?.length ? (
              // The service predates this field, or is unreachable. Say so rather
              // than rendering three convincing zeroes.
              <p className="py-2 text-sm text-muted-foreground" data-testid="tier-breakdown-empty">
                {t('dashboard.tierMix.unavailable')}
              </p>
            ) : (
              <BreakdownList
                data-testid="tier-breakdown"
                // Unlike the status mix, EVERY tier is shown even at zero. «چند
                // کلینیک روی پایه داریم؟» has «هیچ‌کدام» as a real answer, and
                // hiding the row makes that indistinguishable from not knowing.
                rows={metrics.tiers.map((x) => ({
                  key: x.capabilitySet,
                  label: tierLabelKey(x.capabilitySet)
                    ? t(tierLabelKey(x.capabilitySet))
                    : (x.tierNameFa || x.capabilitySet),
                  value: formatNumber(x.clinics || 0),
                  percent: tierTotal ? Math.round(((x.clinics || 0) / tierTotal) * 100) : 0,
                  tone: TIER_TONE[x.capabilitySet] || C.textSecondary,
                  extra: Number(x.mrrRial) > 0
                    ? `${formatMoney(x.mrrRial)} ${isIrr(currency)
                      ? t('common.units.tomanPerMonth')
                      : t('common.units.perMonth', { unit })}`
                    : t('common.value.none'),
                  href: `/clinics?tier=${x.capabilitySet}`,
                }))}
                note={metrics.perpetual > 0
                  ? t('dashboard.tierMix.perpetual', { count: metrics.perpetual })
                  : undefined}
              />
            )}
          </SectionCard>
        </div>
      </div>

      {showGettingStarted ? (
        <div className="mt-5">
          <SectionCard title={t('dashboard.start.title')} icon={Building2}>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">{t('dashboard.start.body')}</p>
              <Button asChild data-testid="dashboard-cta">
                <Link to={`/clinics?status=${UNPROVISIONED}`}>{t('dashboard.start.action')}</Link>
              </Button>
            </div>
          </SectionCard>
        </div>
      ) : null}
    </div>
  );
}
